import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  formatEuroDisplayOnBlur,
  sanitizeEuroDisplay,
  scrollToFirstError,
  toEuroDot,
  validateEuroControl,
} from '../../payments-amount.utils';
import {
  CODICE_FISCALE_PATTERN,
  PaymentSummary,
  formatEuro,
  parseEuroInput,
  quoteServiceFee,
  requireText,
} from '../../payments.models';
import { PaymentSummaryComponent } from '../payment-summary/payment-summary.component';

@Component({
  selector: 'app-lampade-form',
  imports: [ReactiveFormsModule, PaymentSummaryComponent],
  templateUrl: './lampade-form.component.html',
  styleUrl: './lampade-form.component.scss',
})
export class LampadeFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);

  readonly summary = signal<PaymentSummary | null>(null);
  readonly showFormError = signal(false);

  readonly form = this.formBuilder.group({
    numero: ['', Validators.required],
    importo: ['', Validators.required],
    nome: ['', Validators.required],
    cognome: ['', Validators.required],
    codiceFiscale: ['', [Validators.required, Validators.pattern(CODICE_FISCALE_PATTERN)]],
    note: [''],
  });

  get importoDot(): string {
    return toEuroDot(this.form.controls.importo.value ?? '');
  }

  goBack(): void {
    void this.router.navigate(['/pagamenti']);
  }

  onNumeroInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '');
    this.form.controls.numero.setValue(sanitized, { emitEvent: false });
    input.value = sanitized;
    if (this.form.controls.numero.touched) {
      this.validateNumero();
    }
  }

  onNumeroBlur(): void {
    this.form.controls.numero.markAsTouched();
    this.validateNumero();
  }

  onImportoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = sanitizeEuroDisplay(input.value);
    this.form.controls.importo.setValue(sanitized, { emitEvent: false });
    input.value = sanitized;
    if (this.form.controls.importo.touched) {
      validateEuroControl(this.form.controls.importo);
    }
  }

  onImportoBlur(): void {
    const formatted = formatEuroDisplayOnBlur(this.form.controls.importo.value ?? '');
    if (formatted) {
      this.form.controls.importo.setValue(formatted);
    }
    this.form.controls.importo.markAsTouched();
    validateEuroControl(this.form.controls.importo);
  }

  onCodiceFiscaleBlur(): void {
    const value = this.form.controls.codiceFiscale.value?.trim().toUpperCase() ?? '';
    this.form.controls.codiceFiscale.setValue(value);
  }

  submit(): void {
    this.onImportoBlur();
    this.onCodiceFiscaleBlur();

    const { nome, cognome, codiceFiscale, note } = this.form.getRawValue();
    requireText(this.form.controls.nome, nome);
    requireText(this.form.controls.cognome, cognome);
    requireText(this.form.controls.codiceFiscale, codiceFiscale);
    this.validateNumero();
    validateEuroControl(this.form.controls.importo);

    const codice = codiceFiscale?.trim().toUpperCase() ?? '';
    if (codice && !CODICE_FISCALE_PATTERN.test(codice)) {
      this.form.controls.codiceFiscale.setErrors({ pattern: true });
    }

    this.form.markAllAsTouched();

    const numero = Number(this.form.controls.numero.value);
    const numeroValid = Number.isInteger(numero) && numero >= 1;
    const cents = parseEuroInput(this.importoDot);

    if (
      this.form.invalid ||
      !nome?.trim() ||
      !cognome?.trim() ||
      !codice ||
      !numeroValid ||
      cents === null
    ) {
      this.showFormError.set(true);
      scrollToFirstError(this.form);
      return;
    }

    this.showFormError.set(false);
    this.summary.set({
      kind: 'lampade-votive',
      title: 'Lampade Votive',
      tag: 'Servizio Cimiteriale',
      nome: nome!.trim(),
      cognome: cognome!.trim(),
      codiceFiscale: codice,
      extraDetailLabel: 'Numero di lampade',
      extraDetailValue: String(numero),
      amountLabel: 'Importo',
      amountValue: formatEuro(cents),
      note: note?.trim() || undefined,
      baseCents: cents,
      ...quoteServiceFee(cents),
    });
  }

  closeSummary(): void {
    this.summary.set(null);
  }

  pay(): void {
    if (!this.summary()) {
      return;
    }
  }

  private validateNumero(): void {
    const raw = (this.form.controls.numero.value ?? '').trim();
    if (!raw) {
      this.form.controls.numero.setErrors({ required: true });
      return;
    }
    const numero = Number(raw);
    this.form.controls.numero.setErrors(
      Number.isInteger(numero) && numero >= 1 ? null : { whole: true },
    );
  }
}
